import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AppsService } from '../../apps.service';
import { App, CRM_FILTER_OPTIONS, CrmFilters, DEFAULT_CRM_FILTERS } from '../app';

@Component({
  selector: 'rosie-crm-lead-filters',
  templateUrl: './crm-lead-filters.component.html',
  styleUrls: ['./crm-lead-filters.component.scss'],
  standalone: false
})
export class CrmLeadFiltersComponent implements OnInit, OnChanges, OnDestroy {
  @Input() app: App;
  @Input() filters: CrmFilters;
  @Input() modalClosed: Subject<void>;

  readonly options = CRM_FILTER_OPTIONS;
  readonly form = new UntypedFormGroup({
    skipSpam: new UntypedFormControl(DEFAULT_CRM_FILTERS.skipSpam),
    skipHangups: new UntypedFormControl(DEFAULT_CRM_FILTERS.skipHangups),
    skipAnonymous: new UntypedFormControl(DEFAULT_CRM_FILTERS.skipAnonymous)
  });

  private lastSaved: CrmFilters = { ...DEFAULT_CRM_FILTERS };
  private readonly subscriptions = new Subscription();

  constructor(private apps: AppsService, private messageService: MessageService) {
  }

  get hasChanges(): boolean {
    const current = this.form.getRawValue() as CrmFilters;

    return this.options.some(option => current[option.key] !== this.lastSaved[option.key]);
  }

  ngOnInit(): void {
    if (this.modalClosed) {
      this.subscriptions.add(this.modalClosed.subscribe(() => this.reset()));
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.filters) {
      this.lastSaved = { ...DEFAULT_CRM_FILTERS, ...(this.filters || {}) };
      this.reset();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  save(): Observable<boolean> {
    return this.apps.updateCrmFilters(this.app, this.form.getRawValue() as CrmFilters).pipe(
      catchError(() => of(null)),
      map(result => this.handleSaveResult(result))
    );
  }

  private handleSaveResult(result: CrmFilters | null): boolean {
    if (!result) {
      this.messageService.add({ severity: 'error', detail: 'We couldn’t save your new contact filters. Please try again.', life: 5000 });
      return false;
    }

    this.lastSaved = { ...DEFAULT_CRM_FILTERS, ...result };
    this.app.config = { ...this.app.config, crmFilters: this.lastSaved };
    return true;
  }

  private reset(): void {
    this.form.patchValue(this.lastSaved, { emitEvent: false });
  }
}
